import { useEffect, useState } from "react";
import api from "../../api/client";
import { getCompanyBadgeClass } from "../../utils/companyColors";

const STATUS_BADGE = {
  "Pending Review": "bg-yellow-100 text-yellow-800",
  Cancelled: "bg-red-100 text-red-700",
  Activated: "bg-green-100 text-green-700",
};

function StatusBadge({ status }) {
  const cls = STATUS_BADGE[status] || "bg-gray-100 text-gray-700";
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${cls}`}>
      {status}
    </span>
  );
}

function formatDate(d) {
  if (!d) return "-";
  const [y, m, day] = d.split("-");
  return `${m}-${day}-${y}`;
}

/**
 * Embeddable Turn-ins view. Renders the same content as TurnInsModal but
 * without modal chrome — used as a tab body inside XlsxImportsPage.
 */
export default function TurnInsView({ dateRange }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const params = {};
    if (dateRange?.from) params.date_from = dateRange.from;
    if (dateRange?.to) params.date_to = dateRange.to;
    setLoading(true);
    setError(null);
    api.get("/xlsx-imports/turn-ins", { params })
      .then(({ data }) => {
        if (!cancelled) setRecords(data || []);
      })
      .catch(() => {
        if (!cancelled) setError("Failed to load turn-ins.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [dateRange]);

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="flex-1 flex flex-col min-h-0 border rounded-lg overflow-hidden">
        <div className="flex-1 min-h-0 overflow-x-auto overflow-y-auto bg-white">
          {loading ? (
            <div className="p-10 text-center text-gray-500">Loading…</div>
          ) : error ? (
            <div className="p-10 text-center text-red-600">{error}</div>
          ) : records.length === 0 ? (
            <div className="p-10 text-center text-gray-400">No turn-ins for this date range.</div>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 border-b sticky top-0 z-10">
                <tr>
                  <th className="px-2 py-2 w-12 text-center font-medium text-gray-600 whitespace-nowrap bg-gray-50">Count</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600 whitespace-nowrap bg-gray-50">Turn-in Date</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600 whitespace-nowrap bg-gray-50">Dispatch Date</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600 whitespace-nowrap bg-gray-50">Reference ID</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600 whitespace-nowrap bg-gray-50">JO Number</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600 whitespace-nowrap bg-gray-50">Account Number</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600 whitespace-nowrap bg-gray-50">Customer Name</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600 whitespace-nowrap bg-gray-50">Company</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600 whitespace-nowrap bg-gray-50">Assigned TL</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600 whitespace-nowrap bg-gray-50">Sales Agent</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600 whitespace-nowrap bg-gray-50">BO Status</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600 whitespace-nowrap bg-gray-50">Uploaded By</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {records.map((r, idx) => (
                  <tr key={r.id} className="hover:bg-blue-50">
                    <td className="px-2 py-2 w-12 text-center text-gray-500 tabular-nums">{idx + 1}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-700">{formatDate(r.turn_in_date)}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-700">{r.dispatch_date ? formatDate(r.dispatch_date) : <span className="text-gray-400 italic">-</span>}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-700">{r.reference_id || "-"}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-700">{r.jo_number || "-"}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-700">{r.account_number || "-"}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-700 uppercase">{r.customer_full_name || "-"}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {(r.company_prefix || r.company_name) ? (
                        <span className={`text-sm px-2 py-0.5 rounded ${getCompanyBadgeClass(r.company_prefix || r.company_name)}`} title={r.company_name || ""}>{r.company_prefix || r.company_name}</span>
                      ) : (
                        <span className="text-gray-400 italic">-</span>
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {r.assigned_tl_name ? (
                        <span className="text-sm px-2 py-0.5 rounded bg-purple-100 text-purple-800">{r.assigned_tl_name}</span>
                      ) : (
                        <span className="text-gray-400 italic">-</span>
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-700">{r.team_leader_name || "-"}</td>
                    <td className="px-3 py-2 whitespace-nowrap"><StatusBadge status={r.status} /></td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-700">{r.imported_by_name || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      <div className="flex-shrink-0 pt-1 text-sm text-gray-600">
        {loading ? "" : `${records.length} record${records.length === 1 ? "" : "s"}`}
      </div>
    </div>
  );
}
